import React, { useEffect, useMemo, useState } from 'react'
import { Select } from '@st-operations/uikit/src/v2'

import { logErrorAsync } from '_lib/utility'
import { getUnifiedUserProfileSchema } from '_services/data-explorer'
import CustomCheckbox from '_components/custom-checkbox'

const body = document.querySelector('body')

export default function DetailsForm({ data = {}, onDataChange }) {
  const [primaryKeyOptions, setPrimaryKeyOptions] = useState()
  const [mainIdMappingOptions, setMainIdMappingOptions] = useState([])
  const [otherIdMappingOptions, setOtherIdMappingOptions] = useState([])
  const [datasourceOptions, setDatasourceOptions] = useState([])
  const [geoFieldOptions, setGeoFieldOptions] = useState([])
  const [geoTypeOptions, setGeoTypeOptions] = useState([])
  const [degeoFieldOptions, setDegeoFieldOptions] = useState([])

  const primaryKey = data.primaryKey
  const mainIdMappings = data.idMappings?.ids
  const otherIdMappings = data.idMappings?.otherIds
  const flatten = data.idMappings?.flatten
  const datasources = data.datasources
  const includeIso = data.includeIso !== undefined ? data.includeIso : true
  const geoFields = data.geoFields !== undefined ? data.geoFields : ['iso', 'subiso', 'postal']
  const isDegeo = data.geoType === 'degeo'
  const geoTypeSelectValue = data.geoType ?? 'geo'
  const degeoFields = Array.isArray(data.degeoFields) ? data.degeoFields : []

  const validMainIdMappingOptions = useMemo(
    () => mainIdMappingOptions.filter((option) => option !== primaryKey),
    [mainIdMappingOptions, primaryKey]
  )

  const validOtherIdMappingOptions = useMemo(
    () =>
      otherIdMappingOptions.filter((mappingId) => {
        if (primaryKey === 'ip') {
          return mappingId.value !== 'ip'
        } else if (primaryKey === 'estid') {
          return mappingId.value !== 'estid'
        }
        return true
      }),
    [otherIdMappingOptions, primaryKey]
  )

  useEffect(function loadSchemaBasedOptions() {
    async function fetchSchemaBasedOptions() {
      try {
        const schema = await getUnifiedUserProfileSchema()

        setPrimaryKeyOptions(schema.primary_key?.enum)
        setMainIdMappingOptions(schema.id_mappings?.ids?.enum)
        setOtherIdMappingOptions(schema.id_mappings?.other_ids?.enum)
        setDatasourceOptions(schema.datasources?.enum)
        setGeoFieldOptions(schema.geo_fields?.enum)
        setGeoTypeOptions(schema.geo_type?.enum || [])
        setDegeoFieldOptions(schema.degeo_fields?.enum || [])
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }
    fetchSchemaBasedOptions()
  }, [])

  useEffect(
    function makeSureIdMappingsDoesNotContainPrimaryKey() {
      onDataChange?.({
        ...data,
        idMappings: { ids: mainIdMappings?.filter((idMapping) => idMapping !== primaryKey) },
      })
    },
    [primaryKey]
  )

  return (
    <div>
      <Select
        style={{ width: '100%' }}
        isSearchable={true}
        label="Primary Key"
        name="primaryKey"
        onChange={(_, _2, value) => onDataChange?.({ ...data, primaryKey: value })}
        options={primaryKeyOptions}
        value={primaryKey}
        useReactSelect={true}
        menuPosition="fixed"
        menuPortalTarget={body}
        styles={{ menuPortal: (provided) => ({ ...provided, zIndex: 9999, maxHeight: 200 }) }}
      />
      <Select
        style={{ width: '100%' }}
        className="unified-user-profile-wizard-welcome__mapping-id-select"
        name="mapping-id"
        label={
          <div className="flex flex-row w-full justify-between">
            <div>ID Mappings</div>
            <div className="flex flex-row items-center justify-center gap-2">
              <CustomCheckbox
                id="uup-flatten"
                label="Flatten"
                checked={flatten}
                onChange={(e) =>
                  onDataChange({
                    ...data,
                    idMappings: { ...data.idMappings, flatten: e.target.checked },
                  })
                }
              />
            </div>
          </div>
        }
        isSearchable
        isClearable
        value={mainIdMappings}
        options={validMainIdMappingOptions}
        onChange={(_, _2, value) =>
          onDataChange?.({ ...data, idMappings: { ...data.idMappings, ids: value } })
        }
        useReactSelect
        menuPosition="fixed"
        isMulti
        styles={{
          // eslint-disable-next-line no-unused-vars
          menuPortal: ({ left, top, ...provided }) => ({
            ...provided,
          }),
        }}
      />
      <Select
        style={{ width: '100%' }}
        className="unified-user-profile-wizard-welcome__mapping-id-select"
        name="otherIdMappings"
        label={
          <div className="flex flex-row w-full justify-between">
            <div>Other ID Mappings</div>
          </div>
        }
        isSearchable
        isClearable
        value={otherIdMappings}
        options={validOtherIdMappingOptions}
        onChange={(_, _2, value) =>
          onDataChange?.({ ...data, idMappings: { ...data.idMappings, otherIds: value } })
        }
        useReactSelect
        menuPosition="fixed"
        isMulti
        styles={{
          // eslint-disable-next-line no-unused-vars
          menuPortal: ({ left, top, ...provided }) => ({
            ...provided,
          }),
        }}
      />
      <Select
        style={{ width: '100%' }}
        name="datasources"
        label="Data sources"
        isSearchable
        isClearable
        value={datasources}
        options={datasourceOptions}
        onChange={(_, _2, value) => onDataChange?.({ ...data, datasources: value })}
        useReactSelect
        menuPosition="fixed"
        isMulti
        styles={{
          // eslint-disable-next-line no-unused-vars
          menuPortal: ({ left, top, ...provided }) => ({
            ...provided,
          }),
        }}
      />
      <CustomCheckbox
        className="mb-6"
        label="Include ISO"
        checked={includeIso}
        onChange={(e) => {
          onDataChange?.({ ...data, includeIso: e.target.checked })
        }}
      />
      <Select
        style={{ width: '100%' }}
        isSearchable={true}
        label="Geo Type"
        name="geoType"
        onChange={(_, _2, value) =>
          onDataChange?.({ ...data, geoType: value == null ? 'geo' : value })
        }
        options={geoTypeOptions}
        value={geoTypeSelectValue}
        useReactSelect={true}
        menuPosition="fixed"
        menuPortalTarget={body}
        styles={{ menuPortal: (provided) => ({ ...provided, zIndex: 9999, maxHeight: 200 }) }}
      />
      {!isDegeo ? (
        <>
          <Select
            style={{ width: '100%' }}
            name="geoFields"
            label="Geo Fields"
            isSearchable
            isClearable
            value={geoFields}
            options={geoFieldOptions}
            onChange={(_, _2, value) => onDataChange?.({ ...data, geoFields: value })}
            useReactSelect
            menuPosition="fixed"
            isMulti
            styles={{
              // eslint-disable-next-line no-unused-vars
              menuPortal: ({ left, top, ...provided }) => ({
                ...provided,
              }),
            }}
          />
        </>
      ) : (
        <Select
          style={{ width: '100%' }}
          name="degeoFields"
          label="Degeo Fields"
          isSearchable
          isClearable
          value={degeoFields}
          options={degeoFieldOptions}
          onChange={(_, _2, value) => onDataChange?.({ ...data, degeoFields: value })}
          useReactSelect
          menuPosition="fixed"
          isMulti
          menuPortalTarget={body}
          styles={{
            menuPortal: (provided) => ({ ...provided, zIndex: 9999, maxHeight: 200 }),
          }}
        />
      )}
    </div>
  )
}
